import { useState } from 'react';
import type { GameState } from '@/lib/golfEngine';
import { getGameEngine } from '@/games/index';
import { X, Trash2 } from 'lucide-react';
import { DT, dtStatusColor, dtStatusBg } from '@/lib/darkTheme';

interface BetStatusModalProps {
  gameState: GameState;
  onClose: () => void;
  onDeletePress?: (pressId: string) => void;
}

function formatStanding(standing: number): string {
  if (standing === 0) return 'E';
  if (standing > 0) return `+${standing}`;
  return `${standing}`;
}

function formatDollar(amount: number): string {
  if (amount === 0) return '$0';
  if (amount > 0) return `+$${amount}`;
  return `-$${Math.abs(amount)}`;
}

export function BetStatusModal({ gameState, onClose, onDeletePress }: BetStatusModalProps) {
  const engine = getGameEngine(gameState.gameType);
  const status = engine.calculateBetStatus(gameState);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const manualPresses = gameState.gameMode === 'OnDemandPress'
    ? gameState.presses.filter(p => !p.parentPressId)
    : [];

  const handleConfirmDelete = (pressId: string) => {
    if (onDeletePress) {
      onDeletePress(pressId);
    }
    setConfirmDeleteId(null);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={onClose}
      data-testid="bet-status-overlay"
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative rounded-xl shadow-xl mx-4 max-w-md w-full max-h-[80dvh] overflow-y-auto"
        style={{ background: DT.surface, border: `1px solid ${DT.divider}` }}
        onClick={e => e.stopPropagation()}
        data-testid="bet-status-modal"
      >
        <div className="sticky top-0 px-4 py-3 flex items-center justify-between rounded-t-xl" style={{ background: DT.surface, borderBottom: `1px solid ${DT.divider}` }}>
          <h2 style={{ fontWeight: 700, fontSize: '16px', color: DT.primaryText }} data-testid="text-bet-status-title">
            {status.holesCompleted === 0
              ? 'Bet Status \u2014 Hole 0 of 18'
              : `Bet Status \u2014 Hole ${status.lastCompletedHole} of 18`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full transition-colors"
            style={{ color: DT.primaryText, opacity: 0.5 }}
            data-testid="bet-status-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-4">
          {status.holesCompleted === 0 ? (
            <p className="text-center py-4" style={{ fontSize: '14px', color: DT.primaryText, opacity: 0.5 }}>
              Complete at least one hole to see bet status.
            </p>
          ) : (
            <>
              {status.closedBets.length > 0 && (() => {
                const totalNetUnits = status.closedBets.reduce((sum, cb) => sum + cb.netUnitsForA, 0);
                const totalNetDollars = status.closedBets.reduce((sum, cb) => sum + cb.netDollars, 0);
                const totalWinner = totalNetUnits > 0 ? status.teamALabel : totalNetUnits < 0 ? status.teamBLabel : null;

                return (
                  <div data-testid="closed-bets-section">
                    <h3 style={{ color: DT.gold, fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                      Closed Bets
                    </h3>
                    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${DT.divider}` }}>
                      {status.closedBets.map((cb) => {
                        const winner = cb.netUnitsForA > 0 ? status.teamALabel : cb.netUnitsForA < 0 ? status.teamBLabel : null;
                        const netSign = cb.netUnitsForA > 0 ? '+' : '';
                        const absBets = Math.abs(cb.netUnitsForA);
                        return (
                          <div
                            key={cb.leg}
                            className="flex items-center justify-between px-3 py-2.5"
                            style={{ borderBottom: `1px solid ${DT.divider}` }}
                            data-testid={`closed-bet-${cb.leg.toLowerCase()}`}
                          >
                            <span style={{ color: DT.primaryText, fontWeight: 900, fontSize: '16px' }}>{cb.label}:</span>
                            <div className="flex items-center gap-2">
                              {winner && (
                                <span style={{ fontSize: '14px', color: DT.primaryText, opacity: 0.6 }}>{winner}</span>
                              )}
                              <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '18px', color: dtStatusColor(cb.netUnitsForA) }}>
                                {cb.netUnitsForA === 0 ? 'E' : `${netSign}${absBets}`}
                              </span>
                              <span style={{ fontSize: '14px', color: DT.primaryText, opacity: 0.5 }}>
                                {absBets === 1 ? 'bet' : 'bets'}
                              </span>
                              <span style={{ fontSize: '14px', color: dtStatusColor(cb.netUnitsForA) }}>
                                ({formatDollar(cb.netDollars)})
                              </span>
                            </div>
                          </div>
                        );
                      })}

                      {status.closedBets.length > 1 && (
                        <>
                          <div style={{ borderTop: `2px solid ${DT.divider}` }} />
                          <div
                            className="flex items-center justify-between px-3 py-2.5"
                            style={{ background: dtStatusBg(totalNetUnits) }}
                            data-testid="closed-bet-total"
                          >
                            <span style={{ fontSize: '14px', fontWeight: 700, color: DT.primaryText }}>Closed Total:</span>
                            <div className="flex items-center gap-2">
                              {totalWinner && (
                                <span style={{ fontSize: '14px', fontWeight: 600, color: DT.primaryText }}>{totalWinner}</span>
                              )}
                              <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '18px', color: dtStatusColor(totalNetUnits) }}>
                                {totalNetUnits === 0 ? 'E' : `${totalNetUnits > 0 ? '+' : ''}${Math.abs(totalNetUnits)}`}
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: DT.primaryText, opacity: 0.6 }}>
                                {Math.abs(totalNetUnits) === 1 ? 'bet' : 'bets'}
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: 700, color: dtStatusColor(totalNetUnits) }}>
                                ({formatDollar(totalNetDollars)})
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {status.openBetsByLeg.length > 0 && (() => {
                const net = status.openNetSummary;
                const losingTeam = net.netUnitsForA > 0 ? status.teamBLabel : net.netUnitsForA < 0 ? status.teamALabel : null;
                const netDownBets = Math.abs(net.netUnitsForA);
                const netDownDollars = Math.abs(net.netDollarsForA);

                return (
                  <div data-testid="open-bets-section">
                    <h3 style={{ color: DT.gold, fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                      Open Bets ({net.totalActiveBets} active)
                    </h3>
                    <div className="space-y-3">
                      {status.openBetsByLeg.map((legGroup) => (
                        <div key={legGroup.leg}>
                          <p style={{ color: DT.primaryText, fontWeight: 900, fontSize: '16px', marginBottom: '4px' }}>
                            {legGroup.leg} ({legGroup.totalBets} {legGroup.totalBets === 1 ? 'bet' : 'bets'})
                          </p>
                          <div className="space-y-1.5">
                            {legGroup.groups.map((ob) => (
                              <div
                                key={`${legGroup.leg}-${ob.standing}`}
                                className="flex items-center justify-between rounded-lg px-3 py-2"
                                style={{ fontSize: '14px', background: dtStatusBg(ob.standing), border: `1px solid ${DT.divider}` }}
                                data-testid={`open-bet-${legGroup.leg.toLowerCase()}-standing-${ob.standing}`}
                              >
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '16px', color: dtStatusColor(ob.standing) }}>
                                  {formatStanding(ob.standing)}
                                </span>
                                <span style={{ fontWeight: 600, color: dtStatusColor(ob.standing) }}>
                                  x{ob.count}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3" style={{ borderTop: `2px solid ${DT.divider}` }} />
                    <div
                      className="rounded-lg px-3 py-3 mt-2 text-center"
                      style={{ background: losingTeam ? DT.loseBg : DT.darkBg, border: `1px solid ${DT.divider}` }}
                      data-testid="open-bet-net-summary"
                    >
                      {losingTeam ? (
                        <p style={{ fontWeight: 800, fontSize: '20px', color: DT.losing }}>
                          {losingTeam} currently down {netDownBets} {netDownBets === 1 ? 'bet' : 'bets'}
                          <span className="ml-1.5" style={{ fontSize: '18px' }}>(-${netDownDollars})</span>
                        </p>
                      ) : (
                        <p style={{ fontWeight: 800, fontSize: '20px', color: DT.primaryText }}>
                          All open bets currently Even
                        </p>
                      )}
                    </div>

                    <p className="mt-1.5 italic" style={{ fontSize: '10px', color: DT.primaryText, opacity: 0.4 }}>
                      Standings from {status.teamALabel}'s perspective
                    </p>
                  </div>
                );
              })()}

              {onDeletePress && manualPresses.length > 0 && (
                <div data-testid="manual-presses-section">
                  <h3 style={{ color: DT.gold, fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                    Manual Presses
                  </h3>
                  <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${DT.divider}` }}>
                    {manualPresses.map((press) => {
                      const children = gameState.presses.filter(p => p.parentPressId === press.id);
                      const sideLabel = press.initiatedBy === 'top' ? status.teamALabel : status.teamBLabel;
                      const betLabel = press.betType;
                      const isConfirming = confirmDeleteId === press.id;

                      return (
                        <div
                          key={press.id}
                          style={{ borderBottom: `1px solid ${DT.divider}` }}
                          data-testid={`manual-press-${press.id}`}
                        >
                          {!isConfirming ? (
                            <div className="flex items-center justify-between px-3 py-2.5">
                              <div className="flex-1">
                                <span style={{ fontSize: '14px', fontWeight: 600, color: DT.primaryText }}>
                                  {betLabel} Press
                                </span>
                                <span className="ml-2" style={{ fontSize: '12px', color: DT.primaryText, opacity: 0.5 }}>
                                  by {sideLabel} · Hole {press.startingHole}
                                </span>
                                {children.length > 0 && (
                                  <span className="ml-1" style={{ fontSize: '12px', color: DT.primaryText, opacity: 0.4 }}>
                                    (+{children.length} sub)
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(press.id)}
                                className="p-1.5 rounded-lg transition-colors"
                                style={{ color: DT.primaryText, opacity: 0.4 }}
                                data-testid={`btn-delete-press-${press.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: DT.loseBg }}>
                              <span className="flex-1" style={{ fontSize: '12px', color: DT.losing }}>
                                Delete {betLabel} press on Hole {press.startingHole}?
                                {children.length > 0 && ` This also removes ${children.length} sub-press${children.length > 1 ? 'es' : ''}.`}
                              </span>
                              <button
                                type="button"
                                className="px-2 py-1 rounded transition-colors"
                                style={{ fontSize: '12px', fontWeight: 600, color: DT.losing, background: `${DT.losing}22` }}
                                onClick={() => handleConfirmDelete(press.id)}
                                data-testid={`btn-confirm-delete-press-${press.id}`}
                              >
                                Delete
                              </button>
                              <button
                                type="button"
                                className="px-2 py-1"
                                style={{ fontSize: '12px', color: DT.primaryText, opacity: 0.5 }}
                                onClick={() => setConfirmDeleteId(null)}
                                data-testid={`btn-cancel-delete-press-${press.id}`}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div data-testid="projected-total-section">
                <h3 style={{ color: DT.gold, fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  Projected Total
                </h3>
                <div className="rounded-lg px-3 py-2.5 space-y-1.5" style={{ background: DT.darkBg, border: `1px solid ${DT.divider}` }}>
                  <div className="flex items-center justify-between" style={{ fontSize: '14px' }}>
                    <span style={{ fontWeight: 600, color: DT.primaryText }}>{status.teamALabel}</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: dtStatusColor(status.projectedTotalA.dollars) }} data-testid="projected-team-a">
                      {formatDollar(status.projectedTotalA.dollars)}
                      <span className="ml-1.5" style={{ fontSize: '12px', color: DT.primaryText, opacity: 0.4, fontWeight: 400 }}>
                        ({status.projectedTotalA.units > 0 ? '+' : ''}{status.projectedTotalA.units} {Math.abs(status.projectedTotalA.units) === 1 ? 'bet' : 'bets'})
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between" style={{ fontSize: '14px' }}>
                    <span style={{ fontWeight: 600, color: DT.primaryText }}>{status.teamBLabel}</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: dtStatusColor(status.projectedTotalB.dollars) }} data-testid="projected-team-b">
                      {formatDollar(status.projectedTotalB.dollars)}
                      <span className="ml-1.5" style={{ fontSize: '12px', color: DT.primaryText, opacity: 0.4, fontWeight: 400 }}>
                        ({status.projectedTotalB.units > 0 ? '+' : ''}{status.projectedTotalB.units} {Math.abs(status.projectedTotalB.units) === 1 ? 'bet' : 'bets'})
                      </span>
                    </span>
                  </div>
                </div>
                <p className="mt-1.5 italic text-center" style={{ fontSize: '10px', color: DT.primaryText, opacity: 0.4 }}>
                  Projected if all open bets close at current standing
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
